import { ChannelType } from '../types';

export const DEFAULT_CHANNEL_LOGO = '/default-logo.svg';

interface ResolveChannelLogoOptions {
  parentsById: Map<string, ChannelType>;
  isParentsPending: boolean;
  // The host table's own channel list is still loading, so an unknown channel
  // may just not have arrived yet.
  isChannelPending: boolean;
}

/**
 * The parents whose logo the given channels fall back to: those of the
 * channels that have a parent and no logo of their own, each once.
 */
export function parentIdsWithoutOwnLogo(channels: (ChannelType | undefined)[]): string[] {
  const parentIds = new Set<string>();

  for (const channel of channels) {
    if (channel?.parent_channel_id && !channel.parameters?.logo_presigned_url) {
      parentIds.add(channel.parent_channel_id);
    }
  }

  return [...parentIds];
}

/**
 * The logo to show for a channel: its own, otherwise its parent's, otherwise
 * the default one. `null` means "still waiting": the caller keeps an empty slot
 * rather than flashing the default logo before the right one arrives.
 */
export function resolveChannelLogo(
  channel: ChannelType | undefined,
  { parentsById, isParentsPending, isChannelPending }: ResolveChannelLogoOptions
): string | null {
  if (!channel) return isChannelPending ? null : DEFAULT_CHANNEL_LOGO;

  const ownLogo = channel.parameters?.logo_presigned_url;
  if (ownLogo) return ownLogo;

  if (!channel.parent_channel_id) return DEFAULT_CHANNEL_LOGO;

  const parent = parentsById.get(channel.parent_channel_id);
  if (parent) return parent.parameters?.logo_presigned_url || DEFAULT_CHANNEL_LOGO;

  // Not found, or the request failed.
  return isParentsPending ? null : DEFAULT_CHANNEL_LOGO;
}
